import * as Sentry from "@sentry/nextjs";
import { motion } from "motion/react";
import { produce } from "immer";
import { useRouter } from "next/router";
import { RoomResponse } from "queries/chat/rooms/types";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { useChat } from "ui/components/chat/container";
import CheckMark from "ui/components/icons/CheckMark";
import Flag from "ui/components/icons/Flag";
import { Spinner } from "ui/components/icons/Spinner";
import { CHAT_FLAGS } from "ui/lib/constants";
import { handleUpdateRequest } from "ui/lib/useRequest";
import { cn } from "ui/lib/utils";

export const FlagItem = ({ flagType, roomId }) => {
  const actionFlag = CHAT_FLAGS[flagType];
  const { query } = useRouter();
  const [hasError, setHasError] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const queryClient = useQueryClient();

  const { mutate } = useChat();

  const deleteActionFlag = async () => {
    try {
      setIsUpdating(true);
      setIsCompleting(true);
      await handleUpdateRequest(
        `v1/updateLegacyRoom`,
        JSON.stringify({ actionFlag: "", roomID: roomId }),
        "post"
      );
      mutate((current) => {
        return produce(current, (draft) => {
          const channels = draft?.groups?.filter?.(
            ({ id }) => id === query.bootcampId
          );

          channels.map(({ sections }) =>
            sections.map((section) => {
              section.rooms?.map((room) => {
                if (roomId === room?.id) {
                  room.actionFlag = "";
                }
              });
            })
          );
        });
      });

      // Update the cached room data
      queryClient.setQueryData<RoomResponse | undefined>(
        ["room", roomId.split("legacyRoomID:")[1] || roomId],
        (oldData) => {
          return oldData
            ? { ...oldData, room: { ...oldData.room, actionFlag: "" } }
            : oldData;
        }
      );

      setIsUpdating(false);
      setIsCompleting(false);
      setHasError(false);
    } catch (error) {
      setIsUpdating(false);
      setIsCompleting(false);
      setHasError(true);
      Sentry.captureException(error);
    }
  };

  if (!actionFlag) return null;

  return (
    <div>
      {hasError ? (
        <div className="flex px-3 mb-2 space-x-2 border rounded bg-error/25 border-error">
          <span className="text-foreground"> something went wrong</span>
        </div>
      ) : null}
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        initial={isUpdating ? { opacity: 0, y: 50, scale: 1 } : false}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 0, scale: isCompleting ? 1.2 : 1 }}
        onClick={deleteActionFlag}
        disabled={isCompleting}
        className={cn(
          "group py-2 px-4 rounded-full md:rounded shadow-button text-foreground w-full flex-center justify-between bg-neutral-500 md:bg-secondary"
        )}
      >
        <div className="w-full space-x-4 flex-center">
          <div className="justify-between w-full space-x-2 flex-center">
            <Flag
              className={cn("w-5", {
                "text-primary": !hasError,
                "text-error": hasError,
              })}
            />
            <div className="relative w-full max-w-full text-sm text-left truncate">
              <span
                className={cn("truncate", {
                  "line-through": isCompleting,
                })}
              >
                {actionFlag?.label}
              </span>
            </div>
            {isCompleting ? (
              <Spinner className="w-[16px] h-[16px] text-white" />
            ) : (
              <motion.div
                style={{ minWidth: "1.25rem" }}
                className="w-5 h-5 border-2 border-white rounded-md opacity-50 group-hover:opacity-75 flex-center"
              >
                <CheckMark
                  className={cn("w-full opacity-0", {
                    "opacity-100 text-primary": isCompleting,
                    "opacity-0 group-hover:opacity-100": !isCompleting,
                  })}
                />
              </motion.div>
            )}
          </div>
        </div>
      </motion.button>
    </div>
  );
};
